import { formatApiKeyType } from "components/ApiKeys/utils";
import DescriptionDetails from "components/DescriptionDetails";
import FormFieldViewText from "components/FormFields/ViewText";
import FormFieldTimestamp from "components/FormFields/Timestamp";
import Box from "ds/components/Box";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import { useShowFullDescriptionWithBackArrow } from "ds/components/FullDescriptionDrawer/useShowFullDescriptionWithBackArrow";
import Typography from "ds/components/Typography";
import { ApiKeyType } from "types/generated";
import ClaimMappingsTable from "views/Account/Settings/SingleSignOn/components/ClaimMappingsTable";

import ApiKeyExpiresAt from "../components/ExpiresAt";
import { ApiKeyView } from "../types";

type ApiKeysDetailsDrawerProps = {
  apiKey: ApiKeyView;
};

const ApiKeysDetailsDrawer = createDrawer(({ apiKey }: ApiKeysDetailsDrawerProps) => {
  const onOpenFullDescription = useShowFullDescriptionWithBackArrow(apiKey.description);

  const claimMappingEntries = apiKey.oidcSettings?.claimMapping?.entries ?? [];
  const hasClaimMappings = claimMappingEntries.length > 0;

  return (
    <DrawerSimple isDismissable>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="API key details" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody hasDivider={hasClaimMappings}>
        <FormFieldViewText label="Name" value={apiKey.name} />
        <FormFieldViewText label="Key ID" value={apiKey.id} withCopy />
        <FormField label="Type">
          <Typography tag="span" variant="p-body3">
            {formatApiKeyType(apiKey.type)}
          </Typography>
        </FormField>
        {apiKey.type === ApiKeyType.Oidc && (
          <>
            <FormFieldViewText label="Issuer" value={apiKey.oidcSettings?.issuer} />
            <FormFieldViewText label="Client ID (audience)" value={apiKey.oidcSettings?.clientId} />
            <FormFieldViewText
              label="Subject expression"
              value={apiKey.oidcSettings?.subjectExpression}
            />
          </>
        )}
        <FormField label="Expires on" align="start">
          <ApiKeyExpiresAt expiresAt={apiKey.expiresAt} />
        </FormField>
        {apiKey.creator && <FormFieldViewText label="Creator" value={apiKey.creator} />}
        {apiKey.lastUsedAt && (
          <FormFieldTimestamp label="Last used" timestamp={apiKey.lastUsedAt} />
        )}
        <DescriptionDetails
          description={apiKey.description}
          onOpenFullDescription={onOpenFullDescription}
        />
      </DrawerBody>

      {hasClaimMappings && (
        <DrawerBody gap="x-large">
          <Box direction="column" gap="large" fullHeight>
            <Typography variant="p-t6" tag="h3">
              Claim Mappings
            </Typography>
            <ClaimMappingsTable mappings={claimMappingEntries} />
          </Box>
        </DrawerBody>
      )}
    </DrawerSimple>
  );
});

export const showApiKeysDetailsDrawer = createDrawerTrigger(ApiKeysDetailsDrawer);
